import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 19 — Bulk CSV import for the agency directory.
 *
 * Agency owner uploads a CSV → backend `POST /api/agency/businesses/bulk-import`.
 * Each row becomes an UNCLAIMED Business in MongoDB so it renders on /discover
 * with a "Claim this listing" CTA. The owner registers + verifies email/SMS to
 * activate management of menus, payments, bookings, etc.
 */
export default function AgencyBulkImportPage() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  // Build the API base for the template-download anchor (avoids `axios` here)
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  useEffect(() => {
    // Cheap dev-side "history" cached in sessionStorage so the page survives reloads
    try {
      const raw = sessionStorage.getItem("bulkImportHistory");
      if (raw) setHistory(JSON.parse(raw));
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Pick a CSV first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/agency/businesses/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const entry = { at: new Date().toISOString(), filename: file.name, ...data };
      const next = [entry, ...history].slice(0, 10);
      setHistory(next);
      try {
        sessionStorage.setItem("bulkImportHistory", JSON.stringify(next));
      } catch (_e) { /* ignore */ }
      toast.success(`Imported ${data.inserted} new, updated ${data.updated}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bulk-import-page" className="space-y-6 max-w-3xl">
      <header>
        <h1 className="font-serif-display text-3xl text-[#121614]">
          Bulk import restaurants
        </h1>
        <p className="mt-1 text-sm text-[#5C625E]">
          Upload your CSV of venues to ingest them as unclaimed directory listings.
          Each row becomes a public profile on <code>/discover</code> with a
          &nbsp;<em>&quot;Claim this listing&quot;</em>&nbsp; call-to-action. Owners
          can register, verify their email or phone, then drop in their payment,
          calendar, SMS keys and menus — they never need an agency-side ticket.
        </p>
      </header>

      <section className="rounded-2xl border border-black/10 bg-white p-5 space-y-4">
        <a
          href={`${apiBase}/api/agency/businesses/import-template.csv`}
          data-testid="download-template-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#C05746] hover:underline"
        >
          <Download className="h-4 w-4" />
          Download template CSV (recommended columns)
        </a>

        <div className="rounded-xl border-2 border-dashed border-black/10 px-6 py-8 text-center">
          <Upload className="h-6 w-6 mx-auto text-[#5C625E]" />
          <p className="mt-2 text-sm text-[#5C625E]">
            {file ? (
              <>Selected: <span className="font-medium text-[#121614]">{file.name}</span></>
            ) : (
              <>Drop your CSV here or click to choose</>
            )}
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="bulk-import-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              data-testid="bulk-import-pick-btn"
            >
              {file ? "Pick a different file" : "Choose CSV"}
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={!file || busy}
              data-testid="bulk-import-upload-btn"
              className="bg-[#C05746] hover:bg-[#a8442f] text-white"
            >
              {busy ? "Importing…" : "Import now"}
            </Button>
          </div>
        </div>

        <p className="text-xs text-[#5C625E]">
          Required: <code>business_name</code>. Recommended: <code>address1, suburb, state, postcode, phone, email, website, cuisine</code>.
          Unknown columns are kept on the document under <code>import_extra</code> for later use.
        </p>
      </section>

      {result && (
        <section
          data-testid="bulk-import-result"
          className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <p className="font-medium">Import complete</p>
              <p className="mt-1">
                Inserted <strong>{result.inserted}</strong> · Updated{" "}
                <strong>{result.updated}</strong> · Skipped{" "}
                <strong>{result.skipped}</strong> · Batch tag{" "}
                <code className="bg-white/60 px-1 rounded">{result.batch_tag}</code>
              </p>
              {result.skipped > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer">Why some rows were skipped</summary>
                  <pre className="mt-2 text-xs bg-white/60 p-2 rounded overflow-auto">
                    {JSON.stringify(result.skipped_sample, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </section>
      )}

      {history.length > 0 && (
        <section data-testid="bulk-import-history" className="space-y-2">
          <h2 className="font-medium text-[#121614]">Recent imports</h2>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-[#5C625E]">
              <tr>
                <th className="text-left py-1">When</th>
                <th className="text-left py-1">File</th>
                <th className="text-right py-1">Inserted</th>
                <th className="text-right py-1">Updated</th>
                <th className="text-right py-1">Skipped</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-t border-black/8">
                  <td className="py-2">{new Date(h.at).toLocaleString()}</td>
                  <td className="py-2 font-mono text-xs">{h.filename}</td>
                  <td className="py-2 text-right">{h.inserted}</td>
                  <td className="py-2 text-right">{h.updated}</td>
                  <td className="py-2 text-right">{h.skipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
